/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { format, sub } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuButtonSelector, NeuDatepicker, NeuInput } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { logRoundProfileClick } from '../../../utils/analyticsHelpers';
import { date } from '../../../utils/helpers';

/**
 * Style Imports
 */
import {
  DateTimeSelectContainer,
  EmployeeDateTimeSelectContainer,
  FutureTimeErrorSpan,
  StyledLabel
} from './Header.styles';

interface RoundDateTimeSelectProps {
  day: string;
  isViewOnly: boolean;
  selectedTime: string;
  selectedDateTime: string;
  type: string;
  handleDateTimeChange: (e: any) => void;
  setDay: (value: string) => void;
}

const RoundDateTimeSelect: FC<RoundDateTimeSelectProps> = ({
  day,
  isViewOnly,
  selectedTime,
  selectedDateTime,
  type,
  handleDateTimeChange,
  setDay
}) => {
  const { logTrackingEvent } = useAnalyticsApi();

  const [dateSelected, setDateSelected] = useState<string>('');
  const [timeSelected, setTimeSelected] = useState<string>('');

  const maxTime = date.convertToDateTime(new Date());
  const minTime = useMemo(
    () => date.convertToDateTime(sub(new Date(), { days: 7 })),
    []
  );

  useEffect(() => {
    // Emp related function call
    if (type !== 'patient') {
      handleDateTimeChange(
        date.formatDateTimeToString(dateSelected, timeSelected)
      );
    }
  }, [dateSelected, timeSelected]);

  const dateTimeSplit = useMemo(() => {
    // Emp related round timestamp
    const [year, month, rest] = selectedDateTime.split('-');
    const [fullDate, time] = selectedDateTime.split('T');
    setDateSelected(fullDate);
    setTimeSelected(time);
    return { year, month, day: rest.split('T')[0] };
  }, [selectedDateTime]);

  return (
    <>
      {!isViewOnly && type === 'patient' && (
        <NeuButtonSelector
          kind="single"
          size="small"
          style={{ marginTop: '29px', minWidth: '13em' }}
          onNeuChange={(e: any) => {
            setDay(e.detail.value);
          }}
        >
          <NeuButtonSelector
            active={day === 'today'}
            unit="button"
            value="today"
          >
            Today
          </NeuButtonSelector>
          <NeuButtonSelector
            active={day === 'yesterday'}
            unit="button"
            value="yesterday"
          >
            Yesterday
          </NeuButtonSelector>
        </NeuButtonSelector>
      )}
      <DateTimeSelectContainer
        className="d-flex flex-column"
        style={{ minWidth: '10em' }}
      >
        <StyledLabel className="neu-text-body">
          {type === 'patient' ? 'Time' : 'Date & Time'}
        </StyledLabel>
        {type === 'patient' ? (
          <NeuInput
            type="time"
            autocomplete="off"
            disabled={isViewOnly}
            label="Round Time"
            labelHidden
            required
            value={selectedTime}
            style={{ '--form-gap': 0, paddingTop: '4px' }}
            onNeuChange={handleDateTimeChange}
            onNeuFocus={() => logTrackingEvent(logRoundProfileClick('date'))}
          />
        ) : (
          <EmployeeDateTimeSelectContainer>
            <NeuDatepicker
              className="pe-2"
              kind="single"
              active={false}
              minDate={minTime}
              maxDate={maxTime}
              disablePast={false}
              highlightToday
              calendar
              label="Round Date"
              labelHidden
              required
              month={dateTimeSplit.month as any}
              day={dateTimeSplit.day as any}
              year={dateTimeSplit.year as any}
              style={{ '--form-gap': 0, paddingTop: '4px' }}
              onNeuChange={e => {
                setDateSelected(date.convertToDayOfYear(e.detail.value.start));
              }}
            />
            <NeuInput
              type="time"
              autocomplete="off"
              disabled={isViewOnly}
              label="Round Time"
              labelHidden
              required
              value={timeSelected}
              style={{ '--form-gap': 0, paddingTop: '4px' }}
              onNeuChange={e => {
                setTimeSelected(e.detail.value);
              }}
              onNeuFocus={() => logTrackingEvent(logRoundProfileClick('date'))}
            />
          </EmployeeDateTimeSelectContainer>
        )}
        {!isViewOnly &&
          ((day === 'today' && format(new Date(), 'HH:mm') < selectedTime) ||
            selectedDateTime > maxTime) && (
            <FutureTimeErrorSpan>
              Time cannot be in the future
            </FutureTimeErrorSpan>
          )}
      </DateTimeSelectContainer>
    </>
  );
};

export default RoundDateTimeSelect;

// Future use if change to :30 increments - pulled over from old round logic

// <Dropdown
//   style={{
//     width: 267,
//     height: 22,
//     flex: 1,
//     display: 'flex',
//     flexDirection: 'row'
//   }}
//   interface="popover"
//   onNeuChange={onChangeTime}
//   selectedText={timeStringList.current[selectedTimeIndex]}
// >
//   {timeStringList.current.map(time => {
//     return <NeuOption value={time}>{time}</NeuOption>;
//   })}
// </Dropdown>;

// useEffect(() => {
//   if (!currentTime.current) {
//     new Date().toLocaleTimeString();
//     currentTime.current = new Date();

//     timeStringList.current.push(
//       format(currentTime.current.setHours(0, 0, 0, 0), 'hh:mm a')
//     );
//     try {
//       for (
//         let nextTime = add(currentTime.current, { minutes: 30 });
//         compareAsc(nextTime, new Date()) === -1;
//         nextTime = add(nextTime, { minutes: 30 })
//       ) {
//         timeStringList.current.push(format(nextTime, 'hh:mm a'));
//       }
//       timeStringList.current.push(format(new Date(), 'hh:mm a'));
//       timeStringList.current.reverse();
//     } catch (err) {
//       console.log(`ERROR IS :${err}`);
//     }
//   }
// }, [masterTemplateList]);

// useEffect(() => {
//   if (currentTime.current) {
//     currentTime.current = format(new Date(), 'hh:mm');
//   }
// }, [masterTemplateList]);
